// src/components/Ielts_test/hasil/reading/ResultModal.jsx
import { CircleCheck, CircleX } from "lucide-react";
import { useEffect, useState } from "react";

export default function ResultModal({
  result,
  data,
  duration,
  onClose,
  test_type,
}) {
  const [bandScore, setBandScore] = useState(0);

  const resultByPart = data.parts.map((part, pIndex) => {
    const contexts = part.contexts.map((context) => {
      const sections = context.sections.map((section) => {
        const answers = section.questions.map(
          (q) => {
            const a = result.detail_answers.find((ans) => Number(ans.question_id) === q.id) || {
              question_id: q.id,
              number: q.number,
              skipped: true,
              is_correct: false,
            };
            return {
              ...a,
              question: q,
              number: q.number,
            };
          }
        );

        return {
          ...section,
          answers,
        };
      });

      return {
        ...context,
        sections,
      };
    });

    return {
      ...part,
      contexts,
    };
  });

  // Konversi skor ke band (simulasi IELTS)
  useEffect(() => {
    const percent = result.percentage;
    let band = 0;
    if (percent >= 90) band = 9.0;
    else if (percent >= 80) band = 8.0;
    else if (percent >= 70) band = 7.0;
    else if (percent >= 60) band = 6.5;
    else if (percent >= 50) band = 6.0;
    else if (percent >= 40) band = 5.5;
    else if (percent >= 30) band = 5.0;
    else if (percent >= 20) band = 4.0;
    else band = 3.0;
    setBandScore(band);
  }, [result]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const totalQuestions = resultByPart.reduce(
    (sum, part) =>
      sum +
      part.contexts.reduce(
        (contextSum, context) =>
          contextSum +
          context.sections.reduce(
            (sectionSum, section) => sectionSum + section.answers.length,
            0,
          ),
        0,
      ),
    0,
  );

  const correctCount = resultByPart.reduce(
    (sum, part) =>
      sum +
      part.contexts.reduce(
        (contextSum, context) =>
          contextSum +
          context.sections.reduce(
            (sectionSum, section) =>
              sectionSum +
              section.answers.filter(
                (a) => a.is_correct === true || a.is_correct === 1,
              ).length,
            0,
          ),
        0,
      ),
    0,
  );

  return (
    <div className="fixed inset-0 bg-white flex flex-col items-center overflow-y-auto z-50 p-4 md:p-8">
      {/* Header */}
      <div className="w-full max-w-4xl">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-16 h-16 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold text-xl">
              IELTS
            </div>
            <div>
              <h1 className="font-anton text-2xl text-primary">
                IELTS {test_type} Test
              </h1>
              <p className="text-sm text-gray-600">Academic • 60 minutes</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Time & Score */}
        <div className="bg-gray-50 rounded-xl p-4 mb-6 text-center">
          <p className="text-sm text-gray-600">username</p>
          <h2 className="font-anton text-xl text-primary mt-1">
            Your score is:
          </h2>
          <div className="flex justify-center items-center gap-8 mt-4">
            <div className="text-center">
              <div className="w-20 h-20 rounded-full border-4 border-gray-300 flex items-center justify-center text-3xl font-bold text-gray-700">
                {formatTime(duration)}
              </div>
              <p className="text-xs text-gray-600 mt-2">Time Taken</p>
            </div>
            <div className="text-center">
              <div className="w-28 h-28 rounded-full border-8 border-primary flex items-center justify-center text-5xl font-bold text-primary">
                {bandScore}
              </div>
              <p className="text-sm font-poppins mt-2">Band Score</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 rounded-full border-4 border-gray-300 flex items-center justify-center text-3xl font-bold text-gray-700">
                {correctCount}/{totalQuestions}
              </div>
              <p className="text-xs text-gray-600 mt-2">Correct</p>
            </div>
          </div>
        </div>

        {/* Band Score Chart */}
        <div className="bg-white rounded-xl shadow-md p-4 mb-8">
          <p className="text-center text-sm font-poppins text-gray-600 mb-3">
            Band Score:
          </p>
          <div className="flex justify-center gap-1">
            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
              <div
                key={i}
                className={`
                  w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold
                  ${
                    i === Math.floor(bandScore)
                      ? "bg-primary text-white"
                      : "bg-gray-200 text-gray-600"
                  }
                  ${
                    i === Math.ceil(bandScore) && bandScore % 1 !== 0
                      ? "bg-primary text-white"
                      : ""
                  }
                `}
              >
                {i}
              </div>
            ))}
          </div>
        </div>

        {/* Answer Keys */}
        <div className="space-y-6">
          {resultByPart.map((part, pIndex) => (
            <div
              key={`${part.passage_id || part.audio_id}-${pIndex}`}
              className="bg-white rounded-xl shadow-md p-5 space-y-4"
            >
              <h3 className="font-anton text-lg text-primary mb-2">
                Part {pIndex + 1}
              </h3>

              <div className="space-y-4">
                {part.contexts.map((context) => {
                  const contextStart = context.questions[0]?.number;
                  const contextEnd =
                    context.questions[context.questions.length - 1]?.number;

                  return (
                    <div
                      key={context.context_id}
                      className="rounded-xl border border-gray-200 p-4 space-y-4"
                    >
                      <div>
                        <p className="font-poppins text-xs uppercase tracking-wide text-gray-500">
                          Context
                        </p>
                        <h4 className="font-anton text-base text-primary">
                          {context.context_title}
                        </h4>
                        <p className="text-sm text-gray-600">
                          Questions {contextStart}–{contextEnd}
                        </p>
                      </div>

                      <div className="space-y-3">
                        {context.sections.map((section) => (
                          <div
                            key={section.instruksi_id}
                            className="rounded-lg bg-gray-50 p-4"
                          >
                            <p className="font-poppins text-sm font-semibold text-gray-700 mb-3">
                              {section.instruksi_title}
                            </p>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                              {section.answers.map((ans) => {
                                let displayAnswerNode = <span className="flex-1">-</span>;

                                if (!ans.skipped) {
                                  const rawAnswer =
                                    ans.user_answer ??
                                    ans.answare_text ??
                                    ans.selected_option_id ??
                                    ans.option_id;

                                  if (
                                    ans.question?.options &&
                                    rawAnswer !== null &&
                                    rawAnswer !== undefined
                                  ) {
                                    const answersArr = String(rawAnswer).split(", ");
                                    const matchedTexts = answersArr.map(part => {
                                      const opt = ans.question.options.find(
                                        (o) =>
                                          String(o.optionIndex) === String(part) ||
                                          String(o.id) === String(part) ||
                                          String(o.option_text) === String(part),
                                      );
                                      return opt ? opt.option_text : part;
                                    });

                                    displayAnswerNode = (
                                      <div className="flex-1 flex flex-wrap gap-1">
                                        {matchedTexts.map((txt, i) => (
                                          <span key={i} className="px-2 py-0.5 bg-blue-50 text-primary font-medium rounded border border-blue-200">
                                            {txt}
                                          </span>
                                        ))}
                                      </div>
                                    );
                                  } else if (rawAnswer !== null && rawAnswer !== undefined) {
                                    displayAnswerNode = (
                                      <div className="flex-1 flex flex-wrap gap-1">
                                        <span className="px-2 py-0.5 bg-gray-50 text-gray-700 font-medium rounded border border-gray-200">
                                          {String(rawAnswer)}
                                        </span>
                                      </div>
                                    );
                                  }
                                }

                                return (
                                  <div
                                    key={ans.question_id}
                                    className="flex items-start gap-2 text-sm"
                                  >
                                    <span className="font-poppins font-semibold text-gray-700 w-6 mt-1">
                                      {ans.number}.
                                    </span>
                                    {displayAnswerNode}
                                    <div className="mt-0.5">
                                      {ans.is_correct === true || ans.is_correct === 1 ? (
                                        <CircleCheck className="text-green-600 w-5 h-5" />
                                      ) : (
                                        <CircleX className="text-red-600 w-5 h-5" />
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
